import { IconNames } from '@blueprintjs/icons';
import { Icon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';

dayjs.extend(utc);
dayjs.extend(timezone);

// agora-monitor grades SLAs on this calendar, so its times are shown in its zone.
const BUSINESS_TIME_ZONE = 'America/New_York';

// Red is reserved for breaches so they stand out; nearer deadlines share amber and the
// remaining time tells them apart.
const SlaBands = {
    ok: { status: StatusIndicator.Status.DEFAULT },
    warning: { status: StatusIndicator.Status.WARNING },
    urgent: { status: StatusIndicator.Status.WARNING },
    breached: { status: StatusIndicator.Status.CAUTION },
};

// Wire codes from agora-monitor; prose stays in Agora (API returns stored vocabulary only).
const SlaCodeLabels = {
    time_to_acknowledge: 'Time to acknowledge',
    time_to_close: 'Time to close',
};

// Icon = SLA type (not clock state). Paused uses the striped TimeIndicator background.
const SlaCodeIcons = {
    time_to_acknowledge: IconNames.INBOX,
    time_to_close: IconNames.TICK,
};

const formatEasternTime = (utcTime) =>
    `${dayjs.utc(utcTime).tz(BUSINESS_TIME_ZONE).format('ddd M/D h:mm A')} ET`;

// Hours and minutes only: "1d" would read as a calendar day, and a business day is 8 hours.
const formatBusinessMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    return [hours > 0 && `${hours}h`, remainder > 0 && `${remainder}m`].filter(Boolean).join(' ');
};

/** Business time left on the request's soonest running SLA, as graded by agora-monitor. */
export const RequestSlaIndicator = ({
    code,
    minutesRemaining,
    clockState,
    dueUtc,
    resumesAtUtc = undefined,
    band = undefined,
}) => {
    const isOverdue = minutesRemaining <= 0;
    const isPaused = clockState === 'paused';

    const duration = formatBusinessMinutes(Math.abs(minutesRemaining)) || '0m';
    // Minus marks breach; caution band already carries the severity.
    const timeText = isOverdue ? `-${duration}` : duration;

    // Band thresholds stay off the tooltip — the chip already shows remaining time.
    // "Overdue" stays in the tooltip so screen readers hear breach, not a bare negative.
    const tooltipText = [
        SlaCodeLabels[code],
        isPaused &&
            resumesAtUtc &&
            `Paused outside business hours, resumes ${formatEasternTime(resumesAtUtc)}`,
        isOverdue
            ? `Overdue · Was due ${formatEasternTime(dueUtc)}`
            : `Due ${formatEasternTime(dueUtc)}`,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        // Strata TimeIndicator's own classes: timer casing, plus its striped paused background.
        <StatusIndicator
            className={classNames('sds_time-indicator', {
                'sds_time-indicator__container--paused': isPaused,
            })}
            message={
                <span
                    className="flex__gap--x-small"
                    style={{ alignItems: 'center', justifyContent: 'center' }}
                >
                    {SlaCodeIcons[code] && (
                        <Icon icon={SlaCodeIcons[code]} size={12} title={SlaCodeLabels[code]} />
                    )}
                    {timeText}
                </span>
            }
            status={SlaBands[band]?.status ?? StatusIndicator.Status.DEFAULT}
            tooltipText={tooltipText}
        />
    );
};

RequestSlaIndicator.propTypes = {
    code: PropTypes.string.isRequired,
    minutesRemaining: PropTypes.number.isRequired,
    clockState: PropTypes.string.isRequired,
    dueUtc: PropTypes.string.isRequired,
    resumesAtUtc: PropTypes.string,
    band: PropTypes.string,
};
